import React, { Dispatch, SetStateAction } from 'react';
import { IAvatarSkin } from '@interfaces/avatarSkin';
import {
  IEvent,
  IFollower,
  IPixelStreamingClient,
  PlayerSession,
} from '@interfaces/event';
import { IParticipant, IParticipantRegion } from '@interfaces/participants';
import { IRoom } from '@interfaces/rooms';
import { IFollowerData } from '@interfaces/followers';
import { IInitMessage } from '@interfaces/streaming';
import { SendToBriefcasePropsTypeFull } from '../../components/DashboardContent/interfaces';
import type { EditorFocus } from '@core/dashboard/components/EditorMode';
import type {
  MapLayoutRefusal,
  MapLayoutStandingRoom,
} from '@core/dashboard/components/EditorMode/mapEditorUnreal';
import {
  IMillicastEventTokens,
  IMillicastStreamTokens,
  IMillicastTokens,
} from '@interfaces/millicastTokens';
import { IPointOfInterest } from '@interfaces/pointsOfInterest';
import { IShareMediaParams } from '@interfaces/shareMediaParams';
import { IMillicastPublisher } from '@interfaces/millicastPublisher';
import { PixelStreaming } from '@tensorworks/libspsfrontend';
import { PixelStreaming as MytaPixelStreamingClient } from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5';
import { IGamecastVideoStats } from '../../components/DashboardContent/GameCast/interfaces';
import { IMytaStreamsContext } from '../../components/DashboardContent/MytaStreams/interfaces';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { IUser } from '@interfaces/user';
import { AnalyticsType } from '@interfaces/analytics';
import {
  IStartStreamSessionOutput,
  StreamSessionStatus,
} from '@interfaces/gamecast';
import ParticipantScopes from '@constants/scopes';
import { NavigationModeType } from '@constants/navigationMode';

export interface EventParticipantDataType {
  currentEvent?: IEvent;
  currentEventParticipant: IEventParticipant | null;
}

export type UpdateParticipantType = (
  participantId: string,
  newParticipantInfo: Partial<IParticipant>,
) => void;

export interface ParticipantsStateHookProps {
  currentParticipantId: string;
  currentEventId: string | undefined;
  currentParticipant: IParticipant | undefined;
  setCurrentParticipant: React.Dispatch<
    React.SetStateAction<IParticipant | undefined>
  >;
  participants: IParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<IParticipant[]>>;
  currentRoom: IRoom | null;
  setCurrentRoom: React.Dispatch<React.SetStateAction<IRoom | null>>;
  currentRegion: IParticipantRegion | null;
  setCurrentRegion: React.Dispatch<
    React.SetStateAction<IParticipantRegion | null>
  >;
  websocketSessionId?: string;
  setIsParticipantRoomChanged: React.Dispatch<React.SetStateAction<boolean>>;
  rooms: IRoom[];

  trackEventAnalytics: (type: AnalyticsType, payload: any) => void;
}

export type SetParticipantStateType = (
  participantId: string,
  data: {
    eventId?: string | null;
    roomId?: string | null;
    region?: IParticipantRegion | null;
    regions?: IParticipantRegion[];
    gameSessionId?: string | null;
    isDemoUser?: boolean;
    handRaised?: boolean;
    handRaisedAt?: number;
    isSeatRegion?: boolean;
  },
) => void;

export type NewRegionsType = {
  newRegion: IParticipantRegion | null;
  newRegions: IParticipantRegion[];
};

export enum SendMutedStateMessageEnum {
  MUTE = 'MUTE',
  UNMUTE = 'UNMUTE',
}

export enum InitMessageActions {
  INIT = 'INIT',
}

export enum DeviceTypeEnum {
  // Phone Types
  Phone = 'Phone',
  PhoneIOS = 'IOSPhone',
  PhoneAndroid = 'AndroidPhone',
  PhoneWin = 'WindowsPhone',

  // Tablet Types
  Tablet = 'Tablet',
  TabletIOS = 'IOSTablet',
  TabletAndroid = 'AndroidTablet',

  // Desktop Types
  Desktop = 'Desktop',
}

export interface UseInitMessageProps {
  token?: string;
  websocketSessionId: string;
  dolbyToken: string;
  currentParticipant?: IParticipant;
  currentEventParticipant?: IEventParticipant | null;
  currentEvent?: IEvent;
  teleportingToRoom: IRoom | null;
  teleportEnabled: boolean;
  user: IUser;
  userPermissions: ParticipantScopes[];
  generateNewGameliftStreamsSessionKey: () => string;
}

export enum StreamingProviders {
  GameCast = 'GAMECAST',
  CoreWeave = 'COREWEAVE',
  MytaStreams = 'MYTA_STREAMS',
}

export interface IMapFollower {
  pending: IFollowerData[];
  accepted: IFollowerData[];
}

export type MapFollowersType = (followers: IFollower[]) => IMapFollower;

export interface IWebsocketMessageFlag {
  flag: boolean;
  timestamp: number;
}

// export interface IRecreateAvaturnAvatar {
//   avatarId: string;
//   avaturnId: string;
//   url: string;
//   gender: Gender;
// }
export interface IUnrealScreenSize {
  width: number;
  height: number;
}

export interface IMytaverseEventContext {
  currentEventId: string;
  currentParticipantId: string;

  setParticipantsSound: (level: number) => void;
  participantsSound: number;
  assistantSound: number;
  setAssistantSound: (level: number) => void;
  enableSoulmachinesAssistant: boolean;
  setEnableSoulmachinesAssistant: React.Dispatch<React.SetStateAction<boolean>>;
  assistantScriptUpdateTick: number;
  setAssistantScriptUpdateTick: React.Dispatch<React.SetStateAction<number>>;
  eventLoaded: boolean;
  setEventLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setGameSound: (level: number) => void;
  initMessageHandler: () => Promise<IInitMessage | null>;
  gameSound: number;
  rooms: IRoom[];
  reaction: string | null;
  emoji: string | null;
  setEmoji: (emoji: string | null) => void;
  setReaction: (reaction: string | null) => void;
  isHandRaised: boolean;
  setIsHandRaised: React.Dispatch<React.SetStateAction<boolean>>;
  toggleRaiseHand: () => void;
  sharingWindowFullsceen: boolean;
  setSharingWindowFullscreen: () => void;
  handleMinimizeSharingScreen: () => void;
  isMinimizedScreenSharing: boolean;
  setIsMinimizedScreenSharing: React.Dispatch<React.SetStateAction<boolean>>;
  currentEvent: IEvent | undefined;
  setCurrentEvent: React.Dispatch<React.SetStateAction<IEvent | undefined>>;
  currentEventParticipant: IEventParticipant | null;

  currentRoom: IRoom | null | undefined;
  setCurrentRoom: Dispatch<SetStateAction<IRoom | null>>;
  gameCastStreamId: string;
  setGameCastStreamId: Dispatch<SetStateAction<string>>;
  gameCastStreamRegion: string;
  setGameCastStreamRegion: Dispatch<SetStateAction<string>>;
  currentRegion: IParticipantRegion | null;
  setCurrentRegion: (region: IParticipantRegion | null) => void;
  gameCastStreamRequestSended: boolean;
  setGameCastStreamRequestSended: React.Dispatch<React.SetStateAction<boolean>>;
  participants: IParticipant[];
  setParticipants: React.Dispatch<React.SetStateAction<IParticipant[]>>;
  currentParticipant: IParticipant | undefined;
  setCurrentParticipant: React.Dispatch<
    React.SetStateAction<IParticipant | undefined>
  >;
  setCurrentEventParticipant: React.Dispatch<
    React.SetStateAction<IEventParticipant | null>
  >;
  shareMillicastVideoWithSound: boolean;
  setShareMillicastVideoWithSound: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  teleportEnabled: boolean;
  teleportingToRoom: IRoom | null;
  setTeleportingToRoom: Dispatch<SetStateAction<IRoom | null>>;
  teleportToRoom: (room: IRoom | null) => void;
  isTeleportingToRoomByUnreal: boolean;
  setIsTeleportingToRoomByUnreal: Dispatch<SetStateAction<boolean>>;

  teleportToParticipant: (participant: IParticipant | null) => Promise<void>;
  currentRoomDolbySpatialAudioScale: number;
  setRoomDolbySpatialAudioScale: (
    roomId: string,
    dolbySpatialAudioScale: number,
  ) => void;
  updateRoomScale: (value: number) => void;
  previousSkin: IAvatarSkin | null;
  ue5WebsocketConnected: boolean;
  setUe5WebsocketConnected: React.Dispatch<React.SetStateAction<boolean>>;
  selectSkin: (skin: IAvatarSkin) => Promise<void>;
  loading: boolean;
  isTeleporting: boolean;
  setIsTeleporting: React.Dispatch<React.SetStateAction<boolean>>;
  teleportFinished: boolean;
  setTeleportFinished: React.Dispatch<React.SetStateAction<boolean>>;
  openAdminSettingsModal: boolean;
  setOpenAdminSettingsModal: React.Dispatch<React.SetStateAction<boolean>>;

  muted: boolean;
  poiPreviewSrc: SendToBriefcasePropsTypeFull | null;
  setPoiPreviewSrc: React.Dispatch<
    React.SetStateAction<SendToBriefcasePropsTypeFull | null>
  >;
  setMuted: (value?: boolean) => void;
  dolbyToken: string;
  streamChatToken: string;
  streamChatMemberId: string;
  millicastTokens: IMillicastEventTokens | null;
  getMillicastStreamTokens: (
    screenName: string,
  ) => Promise<IMillicastStreamTokens | null>;
  streamingProvider: StreamingProviders | null;
  setStreamingProvider: Dispatch<SetStateAction<StreamingProviders | null>>;
  isNativeProviderCheckPending: boolean;
  mytaStreams: IMytaStreamsContext;
  pointsOfInterest: IPointOfInterest[] | null;
  resetPreviousSkin: () => void;
  userFiles: IPointOfInterest[];
  setUserFiles: React.Dispatch<React.SetStateAction<IPointOfInterest[]>>;
  micUsingByMillicast: boolean;
  setMicUsingByMillicast: React.Dispatch<React.SetStateAction<boolean>>;
  setParticipantState: SetParticipantStateType;
  updateParticipant: UpdateParticipantType;
  recomputeUserEventPermissions: (role: string) => void;
  isDroneMode: boolean;
  setIsDroneMode: React.Dispatch<React.SetStateAction<boolean>>;
  selectNavigationMode: (mode: NavigationModeType) => Promise<void>;

  isShareScreenModalOpen: boolean;
  openShareScreenModal: (params: IShareMediaParams) => Promise<void>;
  closeShareScreenModal: (unlockScreen?: boolean) => Promise<void>;

  openMillicastScreenBroadcastingDataModal: boolean;
  setOpenMillicastScreenBroadcastingDataModal: React.Dispatch<
    React.SetStateAction<boolean>
  >;

  loadingShareScreenModal: boolean;
  setLoadingShareScreenModal: React.Dispatch<React.SetStateAction<boolean>>;

  isShareVideoModalOpen: boolean;
  openShareVideoModal: (params: IShareMediaParams) => Promise<void>;
  closeShareVideoModal: (unlockScreen?: boolean) => Promise<void>;

  loadingShareVideoModal: boolean;
  setLoadingShareVideoModal: React.Dispatch<React.SetStateAction<boolean>>;
  canSpeak: boolean;
  canShowCamera: boolean;
  canShareCamera: boolean;
  canShareScreen: boolean;
  shouldConnectToConference: boolean;
  shareMediaParams?: IShareMediaParams | null;
  setShareMediaParams: React.Dispatch<
    React.SetStateAction<IShareMediaParams | null>
  >;
  gamecastStats: React.MutableRefObject<IGamecastVideoStats | null>;
  showGamecastStats: boolean;
  setShowGamecastStats: React.Dispatch<React.SetStateAction<boolean>>;
  showControls: boolean;
  setShowControls: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentSkin: React.Dispatch<React.SetStateAction<IAvatarSkin | null>>;
  currentSkin: IAvatarSkin | null;

  initMessageSended: boolean;
  openLeaveRegionDialog: boolean;
  setOpenLeaveRegionDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openCameraPublisherDialog: boolean;
  setOpenCameraPublisherDialog: React.Dispatch<React.SetStateAction<boolean>>;
  openTeleportToRoomDialog: boolean;
  setOpenTeleportToRoomDialog: React.Dispatch<React.SetStateAction<boolean>>;
  selectedTeleportRoom: IRoom | null;
  setSelectedTeleportRoom: React.Dispatch<React.SetStateAction<IRoom | null>>;
  initScreenMediaStream: (force?: boolean) => Promise<void>;
  handleStopScreenStream: (streamId: string) => Promise<void>;
  setInitMessageSended: React.Dispatch<React.SetStateAction<boolean>>;
  isStreamLoading: boolean;
  isStreamLoaded: boolean;
  setIsStreamLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsStreamLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  //streamSession: GameCastStreamSession | null;
  shareVideoPublishers: IMillicastPublisher | null;
  setShareVideoPublishers: (publisher: IMillicastPublisher | null) => void;

  shareVideoMediaStream: MediaStream | null;
  setShareVideoMediaStream: React.Dispatch<
    React.SetStateAction<MediaStream | null>
  >;

  shareScreenPublishers?: IMillicastPublisher[];
  setShareScreenPublishers: (publisher: any) => void;
  developersTerminalMessages: string[];
  setDevelopersTerminalMessages: React.Dispatch<React.SetStateAction<string[]>>;
  openDevelopersTerminal: boolean;
  setOpenDevelopersTerminal: React.Dispatch<React.SetStateAction<boolean>>;
  shareScreenMediaStreams?: MediaStream[];
  setShareScreenMediaStreams: React.Dispatch<
    React.SetStateAction<MediaStream[]>
  >;

  resetPreviousCustomSkin: () => void;
  previousCustomAvatar: string | null;
  startShareVideo: (
    shareVideoPublishers: any,
    shareVideoTokens: IMillicastTokens,
    muted: boolean,
  ) => void;
  stopShareVideo: () => void;

  startShareScreen: (
    shareScreenPublishers: IMillicastPublisher,
    shareScreenTokens: IMillicastTokens,
    muted: boolean,
  ) => void;
  stopShareScreen: (streamId: string) => void;
  cleanStates: () => void;

  trackEventAnalytics: (type: AnalyticsType, payload?: any) => void;

  pendingFollowersData: IFollowerData[];
  setPendingFollowersData: Dispatch<SetStateAction<IFollowerData[]>>;
  acceptedFollowersData: IFollowerData[];
  setAcceptedFollowersData: Dispatch<SetStateAction<IFollowerData[]>>;

  handleMillicastWSMessage: (lastJSONMessage: any) => void;

  gameReadyToPlay: boolean;
  setGameReadyToPlay: Dispatch<SetStateAction<boolean>>;

  ue5CoreWeaveDisabled: boolean;
  setUe5CoreWeaveDisabled: Dispatch<SetStateAction<boolean>>;
  pixelStreamingClient: IPixelStreamingClient | null;
  events: IEvent[];
  webSocketAddress: string | null;
  pixelStreaming: PixelStreaming | null;
  mytaPixelStreaming: MytaPixelStreamingClient | null;
  setMytaPixelStreaming: React.Dispatch<
    React.SetStateAction<MytaPixelStreamingClient | null>
  >;
  coreweaveLoading: boolean;
  dataChannelOpened: boolean;
  playStream: boolean;
  connectedPod: string;
  rootElement: HTMLElement | null;
  setRootElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  setPixelStreaming: React.Dispatch<
    React.SetStateAction<PixelStreaming | null>
  >;
  setWebSocketAddress: React.Dispatch<React.SetStateAction<string | null>>;
  togglePlayStream: () => void;
  handleInitializeButton: () => void;
  isPlayButtonPressed: boolean;
  isGamecastCrash: boolean;
  setIsGamecastCrash: React.Dispatch<React.SetStateAction<boolean>>;
  playerSession: PlayerSession | null;
  setPlayerSession: React.Dispatch<React.SetStateAction<PlayerSession | null>>;
  showAvaturnModal: boolean;
  setShowAvaturnModal: React.Dispatch<React.SetStateAction<boolean>>;
  // recreateAvaturnAvatar: IRecreateAvaturnAvatar | null;
  // setRecreateAvaturnAvatar: React.Dispatch<
  //   React.SetStateAction<IRecreateAvaturnAvatar | null>
  // >;
  showExitButton: IWebsocketMessageFlag;
  setShowExitButton: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  isHideWebUI: IWebsocketMessageFlag;
  isHideWebUIExceptAssistant: IWebsocketMessageFlag;
  isHideAllExceptTopBar: IWebsocketMessageFlag;
  setIsHideWebUI: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  setIsHideWebUIExceptAssistant: Dispatch<
    SetStateAction<IWebsocketMessageFlag>
  >;
  setIsHideAllExceptTopBar: Dispatch<SetStateAction<IWebsocketMessageFlag>>;
  isUEModalActionInProgress: boolean;
  setIsUEModalActionInProgress: Dispatch<SetStateAction<boolean>>;
  screenshotDownloadUrl: string | null;
  setScreenshotDownloadUrl: Dispatch<SetStateAction<string | null>>;
  fallbackScreenshotDownloadUrlRef: React.MutableRefObject<string | null>;
  processActiveStreamSession: (
    gamecastStreamSession: IStartStreamSessionOutput,
  ) => Promise<{ isStarted: boolean; isAborted: boolean }>;
  gamecastClientRef: any;
  sendMytaverseActions: (currentEventParticipant: IEventParticipant) => void;
  gameliftStreamsSessionKey: string;
  generateNewGameliftStreamsSessionKey: () => string;
  playVideoUrl: string | null;
  handleGamecastStreamSession: ({
    status,
    gamecastStreamSession,
    sessionKey,
  }: {
    status: StreamSessionStatus;
    gamecastStreamSession: IStartStreamSessionOutput | null;
    sessionKey?: string | undefined;
  }) => Promise<void>;
  setPlayVideoUrl: React.Dispatch<React.SetStateAction<string | null>>;

  showExternalKeyboardOverlay: boolean;
  setShowExternalKeyboardOverlay: Dispatch<SetStateAction<boolean>>;

  externalKeyboardConnected: boolean;
  setExternalKeyboardConnected: Dispatch<SetStateAction<boolean>>;

  isUserEnteringEvent: boolean;
  setIsUserEnteringEvent: Dispatch<SetStateAction<boolean>>;

  showCustomSpotsEditor: boolean;
  setShowCustomSpotsEditor: Dispatch<SetStateAction<boolean>>;
  showCustomizeSpotModal: boolean;
  setShowCustomizeSpotModal: Dispatch<SetStateAction<boolean>>;
  customisationItemSelectedTick: number;
  setCustomisationItemSelectedTick: Dispatch<SetStateAction<number>>;
  selectedCustomizationTag: string | null;
  setSelectedCustomizationTag: Dispatch<SetStateAction<string | null>>;
  roomSpots: any[];
  setRoomSpots: Dispatch<SetStateAction<any[]>>;
  selectedCustomizationUeId: string | null;
  setSelectedCustomizationUeId: Dispatch<SetStateAction<string | null>>;
  selectedCustomizationTargetSize: IUnrealScreenSize | null;
  setSelectedCustomizationTargetSize: Dispatch<
    SetStateAction<IUnrealScreenSize | null>
  >;
  selectedCustomizationSpotType:
    | 'IMAGE'
    | 'VIDEO'
    | 'VIDEO_STATIC'
    | 'QR'
    | null;
  setSelectedCustomizationSpotType: Dispatch<
    SetStateAction<'IMAGE' | 'VIDEO' | 'VIDEO_STATIC' | 'QR' | null>
  >;
  /** Bumped on each UE spot click so EditorMode re-opens the inspector for the same id. */
  customizationSelectionRevision: number;
  setCustomizationSelectionRevision: Dispatch<SetStateAction<number>>;
  isBrandingModeEnabled: boolean;
  setIsBrandingModeEnabled: Dispatch<SetStateAction<boolean>>;

  // Cross-tree signal so EditorMode (rendered in HomeBottomBar) can ask
  // AdminTools (rendered in DashboardTopBar) to open the branding library or
  // the create-branding flow. AdminTools owns the modals; it consumes the
  // request and resets the flag back to null.
  brandingActionRequest: 'library' | 'create' | null;
  setBrandingActionRequest: Dispatch<
    SetStateAction<'library' | 'create' | null>
  >;

  // Which "focus" the editor mode opened with — chosen in EditAreaPickerModal.
  // Used as the initial value for EditorModeProvider; in-editor toggling lives
  // in EditorModeContext.
  brandingEditorFocus: EditorFocus;
  setBrandingEditorFocus: Dispatch<SetStateAction<EditorFocus>>;

  // True while the editor inspector is rendering content (selectedNode !== null
  // in EditorModeContext). The Assistant UI lives outside the editor tree and
  // reads this flag to hide Noa when the inspector occupies the same screen
  // area. The inspector also exposes a button to clear selection and re-open
  // the assistant via `requestAssistantOpen`.
  isEditorInspectorOpen: boolean;
  setIsEditorInspectorOpen: Dispatch<SetStateAction<boolean>>;

  // Incrementing nonce — bumped when the editor's "Open virtual assistant"
  // button is clicked. AssistantProvider watches this to call setOpen(true)
  // without a direct context dependency on the editor.
  assistantOpenRequestNonce: number;
  requestAssistantOpen: () => void;

  // Same cross-boundary pattern as assistantOpenRequestNonce above: the editor
  // (e.g. Editor Mode's "Teleport to trigger") lives outside AssistantProvider,
  // so it can't call useAssistant().triggerRegionChange directly. It calls
  // requestAssistantRegionChange(trigger) instead; AssistantProvider watches
  // pendingAssistantRegionTrigger's nonce and replays the region change.
  pendingAssistantRegionTrigger: { trigger: string; nonce: number } | null;
  requestAssistantRegionChange: (trigger: string) => void;

  // Teleporting to a spot (Inspector's "teleport to spot") makes Unreal echo
  // back a CUSTOMISATION_ITEM_SELECTED message for that same spot shortly
  // after, which would otherwise re-select it and reopen the Inspector pane
  // right after code intentionally closed it. Set this to the spot's ueId
  // right before teleporting; UESelectionBridge checks it and skips exactly
  // one matching re-selection, then clears it.
  suppressedCustomizationSelectIdRef: React.MutableRefObject<string | null>;
  suppressNextCustomizationSelect: (ueId: string) => void;

  currentBrandingId: string | null;
  setCurrentBrandingId: Dispatch<SetStateAction<string | null>>;

  brandingSpots: any[];
  setBrandingSpots: Dispatch<SetStateAction<any[]>>;

  brandingHotspots: any[];
  setBrandingHotspots: Dispatch<SetStateAction<any[]>>;

  pendingEditorHotspotSelection: { hotspotId: string; nonce: number } | null;
  setPendingEditorHotspotSelection: Dispatch<
    SetStateAction<{ hotspotId: string; nonce: number } | null>
  >;
  requestEditorHotspotSelection: (hotspotId: string) => void;

  currentGeometryRooms: MapLayoutStandingRoom[] | null;
  setCurrentGeometryRooms: Dispatch<
    SetStateAction<MapLayoutStandingRoom[] | null>
  >;

  currentGeometryRefusals: MapLayoutRefusal[] | null;
  setCurrentGeometryRefusals: Dispatch<
    SetStateAction<MapLayoutRefusal[] | null>
  >;

  pendingHotspotPlacement: {
    brandingHotspotId: string;
    isCreated: boolean;
  } | null;
  setPendingHotspotPlacement: Dispatch<
    SetStateAction<{
      brandingHotspotId: string;
      isCreated: boolean;
    } | null>
  >;

  familiarizeState: null | { name: string; payload?: any };
  setFamiliariseState: Dispatch<
    SetStateAction<null | { name: string; payload?: any }>
  >;

  resetKeyboardPressedKeys: () => void;

  aiTriggerName: string;
  setAiTriggerName: Dispatch<SetStateAction<string>>;

  objectPoiId: string | null;
  setObjectPoiId: Dispatch<SetStateAction<string | null>>;

  spotFullscreenViewId: string | null;
  setSpotFullscreenViewId: Dispatch<SetStateAction<string | null>>;

  // The AI trigger (region) that owns the clicked spot, sent by UE alongside
  // SPOT_FULLSCREEN_VIEW (`aiTrigger`). Lets the assistant resolve the clicked
  // spot's script by the spot's OWNING region rather than the avatar's current
  // live region — clicking a spot in Expo #2 while standing in Expo #1 must
  // play Expo #2's script.
  spotFullscreenViewTrigger: string | null;
  setSpotFullscreenViewTrigger: Dispatch<SetStateAction<string | null>>;
}
